import {
  AppstoreAddOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LinkOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  LandingPageKind,
  landingPageKindLabels,
  LandingPageStatus,
  type LandingPage,
  type LandingPageConfig,
} from "@fitness-app/data-models/entities/LandingPage";

import { supabase } from "~/store/initializeStore";
import { createDefaultLandingConfig } from "./defaultLandingConfig";
import { LandingEditor } from "./LandingEditor";
import {
  FUNNEL_TEMPLATE_SLUG_PREFIX,
  LANDING_SLUG_PREFIX,
  normalizeFunnelPageSlug,
  normalizeLandingSlug,
} from "./landingSlug";
import { getEcommerceOrigin, getPublicUrl } from "./landingUrls";
import { LandingTemplatesModal } from "./LandingTemplatesModal";
import { useLandingFunnelUsage, useLandings, type LandingFunnelUsage } from "./useLandings";

/** Pages rendered on funnel steps only (TRE-2356): every kind but a landing, and landings under `szablony/`. */
const isFunnelPage = (page: Pick<LandingPage, "kind" | "slug">) =>
  (page.kind ?? LandingPageKind.Landing) !== LandingPageKind.Landing ||
  page.slug.startsWith(FUNNEL_TEMPLATE_SLUG_PREFIX);

type LandingsTab = "landings" | "templates";

const TEMPLATE_KIND_FILTERS = [
  { label: "Wszystkie", value: "all" },
  ...Object.values(LandingPageKind).map((kind) => ({ label: landingPageKindLabels[kind], value: kind })),
];

const describeUsage = (usage: LandingFunnelUsage[]) =>
  usage.map((item) => `${item.funnelName} (${item.stepName})`).join(", ");

const LANDING_STATUS_TAGS: Record<LandingPageStatus, { label: string; color: string }> = {
  [LandingPageStatus.Draft]: { label: "Szkic", color: "gold" },
  [LandingPageStatus.Published]: { label: "Opublikowany", color: "green" },
  [LandingPageStatus.Archived]: { label: "Zarchiwizowany", color: "default" },
};

const createLandingPreviewUrl = async (slug: string, funnelSlug?: string) => {
  const origin = getEcommerceOrigin();
  const token = (await supabase.auth.getSession()).data.session?.access_token;

  if (!origin || !token) {
    throw new Error("Brak aktywnej sesji Supabase.");
  }

  const response = await fetch(`${origin}/api/landing-preview/create`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ slug }),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const data = (await response.json()) as { url: string };
  // A funnel page previews with the data of a funnel using it (prices, checkout product, group).
  return `${origin}${data.url}${funnelSlug ? `&funnel=${encodeURIComponent(funnelSlug)}` : ""}`;
};

const revalidateLanding = async (slug: string) => {
  const origin = getEcommerceOrigin();
  const token = (await supabase.auth.getSession()).data.session?.access_token;

  if (!origin || !token) {
    return;
  }

  // Revalidation is triggered through an authenticated endpoint that verifies the
  // caller's Supabase session server-side, so no shared revalidation secret is
  // ever shipped in the admin bundle.
  await fetch(`${origin}/api/landing-preview/revalidate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ slug }),
  });
};

const openPreview = async (slug: string, funnelSlug?: string) => {
  const previewWindow = window.open("about:blank", "_blank");
  if (previewWindow) {
    previewWindow.opener = null;
  }

  try {
    const previewUrl = await createLandingPreviewUrl(slug, funnelSlug);
    if (previewWindow) {
      previewWindow.location.replace(previewUrl);
    } else {
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    previewWindow?.close();
    message.error(error instanceof Error ? error.message : "Nie udało się otworzyć preview");
  }
};

interface PagesTableProps {
  pages: LandingPage[];
  loading: boolean;
  /** `templates`: funnel step pages, with their kind and the funnels using them. */
  variant: LandingsTab;
  funnelUsage: Map<string, LandingFunnelUsage[]> | undefined;
  onEdit: (page: LandingPage) => void;
  onDuplicate: (page: LandingPage) => Promise<unknown>;
  onArchive: (page: LandingPage) => Promise<void>;
}

const PagesTable = ({ pages, loading, variant, funnelUsage, onEdit, onDuplicate, onArchive }: PagesTableProps) => {
  const isTemplates = variant === "templates";

  return (
    <Table<LandingPage> dataSource={pages} loading={loading} rowKey="id" scroll={{ x: true }}>
      <Table.Column<LandingPage> title="Nazwa" dataIndex="name" />
      {isTemplates ? (
        <Table.Column<LandingPage>
          title="Rodzaj"
          dataIndex="kind"
          render={(kind: LandingPageKind | undefined) => (
            <Tag color="purple">{landingPageKindLabels[kind ?? LandingPageKind.Landing] ?? kind}</Tag>
          )}
        />
      ) : null}
      {isTemplates ? (
        <Table.Column<LandingPage>
          title="Używany w funnelach"
          responsive={["md"]}
          render={(_, row) => {
            const usage = funnelUsage?.get(row.id) ?? [];
            if (!usage.length) return <Typography.Text type="secondary">-</Typography.Text>;
            return (
              <Space size={4} wrap>
                {usage.map((item) => (
                  <Tooltip key={`${item.funnelId}-${item.stepName}`} title={`Krok: ${item.stepName}`}>
                    <Tag color="geekblue">
                      <a href={`/products/${item.productId}`}>{item.funnelName}</a>
                    </Tag>
                  </Tooltip>
                ))}
              </Space>
            );
          }}
        />
      ) : (
        <Table.Column<LandingPage>
          title="Slug"
          dataIndex="slug"
          responsive={["md"]}
          render={(slug: string) => (
            <Typography.Text code className="whitespace-nowrap">
              {slug || "/"}
            </Typography.Text>
          )}
        />
      )}
      <Table.Column<LandingPage>
        title="Status"
        dataIndex="status"
        render={(status: LandingPageStatus) => {
          const tag = LANDING_STATUS_TAGS[status] ?? { label: status, color: "default" };
          return <Tag color={tag.color}>{tag.label}</Tag>;
        }}
      />
      <Table.Column<LandingPage>
        title="Aktualizacja"
        dataIndex="updatedAt"
        responsive={["lg"]}
        render={(updatedAt: string | null, row) => dayjs(updatedAt ?? row.createdAt).format("YYYY-MM-DD HH:mm")}
      />
      <Table.Column<LandingPage>
        title="Publikacja"
        dataIndex="publishedAt"
        responsive={["lg"]}
        render={(publishedAt: string | null) => (publishedAt ? dayjs(publishedAt).format("YYYY-MM-DD HH:mm") : "-")}
      />
      <Table.Column<LandingPage>
        title="Linki"
        render={(_, row) => (
          <Space>
            {isTemplates ? null : (
              <Tooltip title="Kopiuj publiczny URL">
                <Button icon={<LinkOutlined />} onClick={() => navigator.clipboard.writeText(getPublicUrl(row.slug))} />
              </Tooltip>
            )}
            <Tooltip title={isTemplates ? "Podgląd z danymi funnelu, który używa strony" : "Otwórz preview"}>
              <Button
                icon={<EyeOutlined />}
                onClick={() => openPreview(row.slug, funnelUsage?.get(row.id)?.[0]?.funnelSlug)}
              />
            </Tooltip>
          </Space>
        )}
      />
      <Table.Column<LandingPage>
        title="Akcje"
        render={(_, row) => (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => onEdit(row)}>
              Edytuj
            </Button>
            <Tooltip title="Zrób kopię">
              <Button
                icon={<CopyOutlined />}
                onClick={async () => {
                  await onDuplicate(row);
                  message.success("Kopia utworzona");
                }}
              />
            </Tooltip>
            <Popconfirm
              title={isTemplates ? "Zarchiwizować szablon?" : "Zarchiwizować landing?"}
              description={
                funnelUsage?.get(row.id)?.length
                  ? `Używają go: ${describeUsage(funnelUsage.get(row.id)!)}. Te kroki wrócą do szablonu z kodu.`
                  : undefined
              }
              onConfirm={async () => {
                await onArchive(row);
                message.success(isTemplates ? "Szablon zarchiwizowany" : "Landing zarchiwizowany");
              }}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )}
      />
    </Table>
  );
};

const Landings = () => {
  const {
    data = [],
    isLoading,
    createLanding,
    saveDraft,
    publish,
    duplicate,
    archive,
    importTemplates,
    getTakenSlugs,
  } = useLandings();
  const { data: funnelUsage } = useLandingFunnelUsage();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [editedLanding, setEditedLanding] = useState<LandingPage | null>(null);
  const [kindFilter, setKindFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: LandingsTab = searchParams.get("tab") === "templates" ? "templates" : "landings";
  const setTab = (next: LandingsTab) =>
    setSearchParams((params) => {
      if (next === "templates") params.set("tab", next);
      else params.delete("tab");
      return params;
    });
  const [form] = Form.useForm<{ name: string; slug: string; kind: LandingPageKind }>();
  const createsTemplate = tab === "templates";

  const landingPages = data.filter((page) => !isFunnelPage(page));
  const templatePages = data.filter(
    (page) => isFunnelPage(page) && (kindFilter === "all" || (page.kind ?? LandingPageKind.Landing) === kindFilter),
  );

  // `/landings?edit=<id>` opens the editor (and its tab), e.g. from a funnel step's "Edytuj stronę".
  const editId = searchParams.get("edit");
  useEffect(() => {
    if (!editId || !data.length) return;
    const page = data.find((item) => item.id === editId);
    if (page) setEditedLanding(page);
    setSearchParams((params) => {
      params.delete("edit");
      if (page && isFunnelPage(page)) params.set("tab", "templates");
      return params;
    });
  }, [editId, data, setSearchParams]);

  const handleCreate = async () => {
    const values = await form.validateFields();
    await createLanding({
      name: values.name,
      slug: createsTemplate ? normalizeFunnelPageSlug(values.slug) : normalizeLandingSlug(values.slug),
      kind: createsTemplate ? (values.kind ?? LandingPageKind.Landing) : LandingPageKind.Landing,
    });
    message.success(createsTemplate ? "Szablon utworzony" : "Landing utworzony");
    setCreateModalOpen(false);
    form.resetFields();
  };

  const handlePublish = async (
    landing: LandingPage,
    config: LandingPageConfig,
    fields: { name: string; slug: string },
  ) => {
    await publish(landing, config, fields);
    // Funnel pages have no route of their own; their funnel step is server-rendered.
    if (!isFunnelPage({ kind: landing.kind, slug: fields.slug })) {
      await revalidateLanding(fields.slug);
    }
  };

  const tableProps = {
    loading: isLoading,
    funnelUsage,
    onEdit: setEditedLanding,
    onDuplicate: (page: LandingPage) => duplicate(page),
    onArchive: (page: LandingPage) => archive(page.id),
  };

  return (
    <Card
      title="Landingi"
      extra={
        tab === "templates" ? (
          <Space wrap>
            <Button icon={<AppstoreAddOutlined />} onClick={() => setTemplatesModalOpen(true)}>
              Szablony z oferty
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
              Nowy szablon
            </Button>
          </Space>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            Dodaj landing
          </Button>
        )
      }
    >
      <Tabs
        activeKey={tab}
        onChange={(key) => setTab(key as LandingsTab)}
        items={[
          {
            key: "landings",
            label: `Landingi (${landingPages.length})`,
            children: <PagesTable {...tableProps} pages={landingPages} variant="landings" />,
          },
          {
            key: "templates",
            label: `Szablony funnelu (${data.length - landingPages.length})`,
            children: (
              <>
                <Segmented
                  className="mb-4"
                  options={TEMPLATE_KIND_FILTERS}
                  value={kindFilter}
                  onChange={(value) => setKindFilter(String(value))}
                />
                <PagesTable {...tableProps} pages={templatePages} variant="templates" />
              </>
            ),
          },
        ]}
      />

      <Modal
        title={createsTemplate ? "Nowy szablon funnelu" : "Nowy landing"}
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreate}
      >
        <Form form={form} layout="vertical" initialValues={{ name: "", slug: "", kind: LandingPageKind.Landing }}>
          <Form.Item name="name" label="Nazwa" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {createsTemplate ? (
            <Form.Item name="kind" label="Krok funnelu">
              <Select
                options={Object.values(LandingPageKind).map((kind) => ({
                  label: landingPageKindLabels[kind],
                  value: kind,
                }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="slug" label="Slug">
            <Input
              addonBefore={createsTemplate ? `/${FUNNEL_TEMPLATE_SLUG_PREFIX}` : `/${LANDING_SLUG_PREFIX}`}
              placeholder={createsTemplate ? "moj-checkout" : "test2026"}
            />
          </Form.Item>
        </Form>
      </Modal>

      <LandingTemplatesModal
        open={templatesModalOpen}
        onClose={() => setTemplatesModalOpen(false)}
        getTakenSlugs={getTakenSlugs}
        importTemplates={importTemplates}
      />

      {editedLanding ? (
        <LandingEditor
          key={editedLanding.id}
          landing={{
            ...editedLanding,
            draftConfig: editedLanding.draftConfig ?? createDefaultLandingConfig(editedLanding.slug),
          }}
          open={Boolean(editedLanding)}
          onClose={() => setEditedLanding(null)}
          onSaveDraft={saveDraft}
          onPublish={handlePublish}
        />
      ) : null}
    </Card>
  );
};

export default Landings;
